import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const neighborhoods = [
  { name: 'Centre', position: 'top-1/4 left-1/3' },
  { name: 'Casagemes', position: 'top-1/3 left-1/4' },
  { name: 'Gorg', position: 'top-1/2 left-1/5' },
  { name: 'Llefià', position: 'top-1/3 right-1/4' },
  { name: 'La Salut', position: 'top-2/3 left-1/3' },
  { name: 'Artigues', position: 'top-1/2 right-1/3' },
  { name: 'Sant Roc', position: 'top-3/4 left-1/4' },
  { name: 'Bufalà', position: 'top-1/4 right-1/4' },
  { name: 'Morera', position: 'top-2/3 right-1/4' },
  { name: 'Canyet', position: 'top-1/2 left-1/2' },
];

const ServiceAreasSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section ref={ref} className="py-16 sm:py-24 bg-gradient-to-b from-dark-bg to-dark-card px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block px-4 py-2 bg-orange-primary/10 border border-orange-primary rounded-full mb-6">
              <span className="text-orange-primary text-sm font-bold">Zonas de servicio</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-text-primary mb-6 break-words">
              Servicio en Badalona y alrededores
            </h2>
            
            <p className="text-text-secondary text-base sm:text-lg leading-relaxed mb-8">
              Realizamos instalaciones de pladur en todos los barrios de Badalona: Centre, Casagemes, Dalt la Vila, Progrés, Gorg, Llefià, La Salut, Artigues, Sant Roc, Bufalà, Morera, Canyet, Montigalà, Pomar y Manresà.
            </p>

            <div className="space-y-4 mb-10">
              <div className="flex items-start space-x-3">
                <i className="ri-map-pin-line text-orange-primary text-xl mt-1 w-6 h-6 flex items-center justify-center flex-shrink-0"></i>
                <div>
                  <h3 className="text-text-primary font-bold mb-1 text-sm sm:text-base">Badalona completa</h3>
                  <p className="text-text-muted text-xs sm:text-sm">Todos los barrios y zonas de la ciudad</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-map-pin-line text-orange-primary text-xl mt-1 w-6 h-6 flex items-center justify-center flex-shrink-0"></i>
                <div>
                  <h3 className="text-text-primary font-bold mb-1 text-sm sm:text-base">Barcelona y alrededores</h3>
                  <p className="text-text-muted text-xs sm:text-sm">Sant Martí, Eixample, Santa Coloma, Sant Adrià, Montgat, Tiana</p>
                </div>
              </div>
            </div>

            <Link
              to="/contacto"
              className="inline-flex items-center space-x-2 px-6 sm:px-8 py-3 sm:py-4 bg-orange-primary text-white rounded-full hover:shadow-glow-orange transition-all duration-300 font-bold text-sm sm:text-base whitespace-nowrap"
            >
              <span>Consultar disponibilidad</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>

          {/* Right Column - Map Visualization */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative h-[400px] sm:h-[500px] bg-dark-bg border border-dark-border rounded-2xl p-6 sm:p-8 overflow-hidden"
          >
            {/* Map Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="w-full h-full" style={{
                backgroundImage: 'radial-gradient(circle, #FF6A00 1px, transparent 1px)',
                backgroundSize: '30px 30px'
              }}></div>
            </div>

            {/* Neighborhood Badges */}
            {neighborhoods.map((neighborhood, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.4, delay: 0.3 + 0.1 * index }}
                className={`absolute ${neighborhood.position} transform -translate-x-1/2 -translate-y-1/2`}
              >
                <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-dark-card border border-orange-primary rounded-full hover:scale-110 hover:shadow-glow-orange transition-all duration-300 cursor-pointer whitespace-nowrap">
                  <span className="text-text-primary text-xs sm:text-sm font-medium">{neighborhood.name}</span>
                </div>
              </motion.div>
            ))}

            {/* Center Pin */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            >
              <div className="relative">
                <div className="w-12 sm:w-16 h-12 sm:h-16 bg-orange-primary rounded-full flex items-center justify-center shadow-glow-orange">
                  <i className="ri-map-pin-fill text-white text-xl sm:text-2xl"></i>
                </div>
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-orange-primary"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 0, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreasSection;
